import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConsolidationSuggestion } from '../../../../types/consolidation';
import { SourceSelectionState } from './types';
import { parsedQty } from './utils';

const keyFor = (suggestionIndex: number, fromBinCode: string): string =>
    `${suggestionIndex}|${fromBinCode}`;

/**
 * Felles valg-state for kildelister i «Trekk inn hit» og «Samle vare».
 * Alle kilder forhåndsavhukes med foreslått kvantum når forslagene lastes.
 */
export const useSourceSelections = (suggestions: ConsolidationSuggestion[]) => {
    const [selections, setSelections] = useState<Record<string, SourceSelectionState>>({});

    useEffect(() => {
        const next: Record<string, SourceSelectionState> = {};
        suggestions.forEach((suggestion, index) => {
            suggestion.sources.forEach((source) => {
                next[keyFor(index, source.fromBinCode)] = {
                    // Kapasitets-begrensede kilder (Compass tror det ikke er plass) starter
                    // avhuket NEI — operatøren huker dem på bevisst etter å ha oppdatert kapasiteten.
                    checked: !source.capacityLimited,
                    qty: String(source.quantity)
                };
            });
        });
        setSelections(next);
    }, [suggestions]);

    const selectionFor = useCallback(
        (suggestionIndex: number, fromBinCode: string): SourceSelectionState =>
            selections[keyFor(suggestionIndex, fromBinCode)] ?? { checked: true, qty: '' },
        [selections]
    );

    const toggle = useCallback((suggestionIndex: number, fromBinCode: string) => {
        setSelections((prev) => {
            const key = keyFor(suggestionIndex, fromBinCode);
            const current = prev[key] ?? { checked: true, qty: '' };
            return { ...prev, [key]: { ...current, checked: !current.checked } };
        });
    }, []);

    const setQty = useCallback((suggestionIndex: number, fromBinCode: string, qty: string) => {
        setSelections((prev) => {
            const key = keyFor(suggestionIndex, fromBinCode);
            const current = prev[key] ?? { checked: true, qty: '' };
            return { ...prev, [key]: { ...current, qty } };
        });
    }, []);

    /** Antall avhukede kilder med antall > 0 (linjetelleren i hovedknappen). */
    const selectedCount = useMemo(
        () =>
            Object.values(selections).filter(
                (selection) => selection.checked && parsedQty(selection.qty) > 0
            ).length,
        [selections]
    );

    return { selectionFor, toggle, setQty, selectedCount };
};
